import { useEffect, useState, useRef } from 'react';
import { getRequests, getDailyTotals } from '../api';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import CustomTooltip from '../components/CustomTooltip';
import { formatTableDate, formatTimeWithSeconds } from '../utils/formatDate';

export default function Requests({ refreshKey }) {
  // Lazy initialization: read cached data (if any) synchronously.
  const [requests, setRequests] = useState(() => {
    const cached = localStorage.getItem('requestsData');
    return cached ? JSON.parse(cached) : [];
  });
  const [dailyTotals, setDailyTotals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(() => {
    const cached = localStorage.getItem('requestsData');
    return cached ? JSON.parse(cached).length : 0;
  });
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef(null);
  const limit = 30;
  // Auto-refresh state to trigger effect every 5 seconds.
  const [autoRefreshKey, setAutoRefreshKey] = useState(0);

  // Persist requests data in localStorage whenever it changes.
  useEffect(() => {
    localStorage.setItem('requestsData', JSON.stringify(requests));
  }, [requests]);

  async function loadRequests(currentOffset = offset) {
    // When loading additional pages (currentOffset !== 0), do nothing if no more data.
    if (currentOffset !== 0 && !hasMore) return;
    setLoading(true);
    try {
      const data = await getRequests(currentOffset);
      if (data.length > 0) {
        setRequests(prev => [...prev, ...data]);
        setOffset(currentOffset + data.length);
        if (data.length < limit) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  }

  // Combined refresh effect (manual via refreshKey and auto via autoRefreshKey)
  // that fetches the first page and merges in new rows without clearing the table.
  useEffect(() => {
    async function fetchLatest() {
      try {
        const newData = await getRequests(0);
        setRequests(prev => {
          const existingIds = new Set(prev.map(item => item.request_id));
          const newItems = newData.filter(item => !existingIds.has(item.request_id));
          if (newItems.length > 0) {
            setOffset(oldOffset => oldOffset + newItems.length);
            return [...newItems, ...prev];
          }
          return prev;
        });
      } catch (error) {
        console.error('Failed to fetch latest requests:', error);
      }
    }
    fetchLatest();
  }, [autoRefreshKey, refreshKey]);

  // Fetch daily totals for requests (last 30 days)
  useEffect(() => {
    async function fetchDailyTotals() {
      try {
        const totals = await getDailyTotals('requests');
        setDailyTotals(totals);
      } catch (error) {
        console.error('Error fetching daily totals:', error);
      }
    }
    fetchDailyTotals();
  }, []);

  // Auto-refresh interval: update autoRefreshKey every 5 seconds.
  useEffect(() => {
    const intervalId = setInterval(() => {
      setAutoRefreshKey(prev => prev + 1);
    }, 5000);
    return () => clearInterval(intervalId);
  }, []);

  function lastRequestRef(node) {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        console.log('Last element is visible, loading more...');
        loadRequests(offset);
      }
    });
    if (node) observer.current.observe(node);
  }

  // Convert status to a number to avoid lookup issues.
  const getStatusText = code => {
    const status = Number(code);
    const statusMap = {
      200: 'Success',
      400: 'Bad Request',
      403: 'Forbidden',
      500: 'Server Error'
    };
    return statusMap[status] || 'Unknown';
  };

  const getStatusColor = status => (Number(status) === 200 ? 'success' : 'error');

  return (
    <div className="requests-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Request Logs</h2>
        <div style={{ width: '300px', height: '50px' }}>
          {dailyTotals.length > 0 && (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyTotals}>
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="rgb(234, 88, 12)" 
                  strokeWidth={2} 
                  dot={false} 
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th style={{ width: '120px' }}>Date</th>
              <th style={{ width: '150px' }}>Endpoint</th>
              <th className="domain-cell">Domain</th>
              <th className="file-cell">File</th>
              <th>Status</th>
              <th>Client IP</th>
              <th>User Agent</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request, index) => (
              <tr key={request.request_id} ref={index === requests.length - 1 ? lastRequestRef : null}>
                <td>{request.request_id}</td>
                <td title={formatTimeWithSeconds(request.requested_at)}>
                  {formatTableDate(request.requested_at)}
                </td>
                <td className="endpoint">{request.endpoint}</td>
                <td className="domain-cell">{request.origin_domain}</td>
                <td className="file-cell" title={request.original_file_name}>
                  {request.original_file_name}
                </td>
                <td>
                  <span
                    className={`status ${getStatusColor(request.response_status)}`}
                    style={{ margin: 0 }}
                  >
                    {request.response_status} - {getStatusText(request.response_status)}
                  </span>
                </td>
                <td>{request.client_ip}</td>
                <td>{request.user_agent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hasMore && <p className="no-more-logs">No more logs to load.</p>}
    </div>
  );
}
